import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, animate } from "motion/react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { fetchNotifications, markNotificationRead } from "../utils/api";

const itemVariants = {
  hidden: { opacity: 0, y: -30, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
};

function PanelRoot({ children, isShowBackground = false }) {
  return (
    <div className="h-full flex flex-col relative isolate">
      <AnimatePresence>
        {isShowBackground && (
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(3px)",
              maskImage:
                "linear-gradient(to right, black 90%, rgba(0, 0, 0, 0.5) 97%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, black 90%, rgba(0, 0, 0, 0.5) 97%, transparent 100%)",
              pointerEvents: "none",
              zIndex: -1,
            }}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>
      {children}
    </div>
  );
}

function timeAgo(timestamp) {
  const now = Date.now();
  const ts = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  const diff = now - ts;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Vừa xong";
  if (minutes < 60) return `${minutes} phút trước`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  return `${days} ngày`;
}

function cardSurface(isRead) {
  return !isRead
    ? { bg: "bg-[#F5F5F5]", fill: "fill-[#F5F5F5]" }
    : { bg: "bg-[#FFFFFF]", fill: "fill-[#FFFFFF]" };
}

function getCardBg(isRead) {
  return cardSurface(isRead).bg;
}

function getCardFill(isRead) {
  return cardSurface(isRead).fill;
}

function getTextColor(isRead) {
  return !isRead ? "text-[#2A2A2F]" : "text-[#636369]";
}

function getTimeAgoColor(isRead) {
  return !isRead ? "text-[#2A2A2F]" : "text-[#A0A0A5]";
}

function getCardHoverBorder(showTail) {
  return showTail
    ? "sm:group-hover:border-[#636369]"
    : "sm:group-hover:border-[#C5C5C7]";
}

function getCardBorder(showTail) {
  return showTail ? "border-[#C5C5C7]" : "border-[#ECECED]";
}

function getTailStrokeClass(showTail) {
  return showTail
    ? "stroke-[#C5C5C7] sm:group-hover:stroke-[#636369] transition-colors duration-300"
    : "stroke-[#ECECED] sm:group-hover:stroke-[#C5C5C7] transition-colors duration-300";
}

function BubbleTail({ fillClass, strokeClass }) {
  return (
    <svg
      width="13"
      height="17"
      viewBox="-1 16 15.18 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.18 32.5L1.09062 19.9141C-0.169014 18.6542 0.723147 16.5002 2.50468 16.5H14.18V32.5Z"
        className={`${fillClass} transition-colors duration-300`}
      />
      <path
        d="M13.6766 32.5L1.09062 19.9141C-0.169014 18.6542 0.723147 16.5002 2.50468 16.5H13.6766"
        className={`fill-none ${strokeClass}`}
        strokeWidth="1"
      />
    </svg>
  );
}

function NebulaAvatar({ textColorClass = "text-zinc-800" }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className="w-5 h-5 rounded-full bg-[#3f3f46] text-white flex items-center justify-center text-[10px] font-bold">
        n
      </div>
      <span className={`text-[14px] font-semibold ${textColorClass}`}>
        Nebula
      </span>
    </div>
  );
}

function StateBubble({ children }) {
  return (
    <div className="pt-[8px] sm:pt-[108px] px-4">
      <div className="relative flex flex-col gap-1.5 w-full pr-0 pl-0 mb-5">
        <div className="relative">
          <div className="absolute -left-[12px] top-[16px] z-20">
            <BubbleTail fillClass="fill-white" strokeClass="stroke-[#C5C5C7]" />
          </div>
          <div className="relative z-10 bg-white border border-[#C5C5C7] rounded-[16px] p-4 shadow-sm">
            <NebulaAvatar />
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationCardContent({ notification, showTail, isRead, onClick }) {
  return (
    <>
      <AnimatePresence>
        {showTail && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, x: 5 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.5, x: 5 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -left-[12px] top-[16px] z-20"
          >
            <BubbleTail
              fillClass={getCardFill(isRead)}
              strokeClass={getTailStrokeClass(showTail)}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <div
        onClick={onClick}
        className={`relative z-10 ${getCardBg(isRead)} border rounded-[16px] p-[16px] shadow-sm transition-all duration-300 cursor-pointer ${getCardHoverBorder(showTail)} ${getCardBorder(showTail)}`}
      >
        <NebulaAvatar textColorClass={getTextColor(isRead)} />
        <p className={`text-[14px] leading-relaxed ${getTextColor(isRead)}`}>
          {notification.summary || notification.title}
        </p>
      </div>
    </>
  );
}

function NotificationPanel({ onClose, onNotificationClick, count = 0 }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(() => window.innerWidth < 640);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const scrollTimeoutRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const scrollAnimationRef = useRef(null);
  const activeIndexRef = useRef(0);
  const scrollRafId = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await fetchNotifications("nebula", 0, 30, "");
        if (!cancelled) setNotifications(response?.data || []);
      } catch (e) {
        console.error("Failed to load notifications:", e);
        if (!cancelled) setError("Không thể tải thông báo. Vui lòng thử lại.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = async (notification) => {
    if (!notification.readStatus) {
      try {
        await markNotificationRead("nebula", notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, readStatus: true } : n,
          ),
        );
      } catch (e) {
        console.error("Failed to mark notification as read:", e);
      }
    }
    onNotificationClick(notification);
  };

  const scrollToIndex = (index) => {
    const container = containerRef.current;
    const item = itemRefs.current[index];
    if (!container || !item) return;

    const targetScrollTop = item.offsetTop - 84;

    isProgrammaticScroll.current = true;
    activeIndexRef.current = index;
    setActiveIndex(index);

    if (scrollAnimationRef.current) {
      scrollAnimationRef.current.stop();
    }

    scrollAnimationRef.current = animate(container.scrollTop, targetScrollTop, {
      type: "spring",
      stiffness: 300,
      damping: 25,
      mass: 1,
      onUpdate: (latest) => {
        if (containerRef.current) {
          containerRef.current.scrollTop = latest;
        }
      },
      onComplete: () => {
        setTimeout(() => {
          isProgrammaticScroll.current = false;
          scrollAnimationRef.current = null;
        }, 50);
      },
    });
  };

  const handleScroll = () => {
    if (!containerRef.current || !isExpanded) return;
    if (scrollRafId.current !== null) return;

    scrollRafId.current = requestAnimationFrame(() => {
      scrollRafId.current = null;
      if (!containerRef.current) return;

      const scrollTop = containerRef.current.scrollTop;
      let closestIndex = 0;
      let minDistance = Infinity;

      itemRefs.current.forEach((item, index) => {
        if (!item) return;
        const distance = Math.abs(item.offsetTop - 84 - scrollTop);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== activeIndexRef.current) {
        activeIndexRef.current = closestIndex;
        setActiveIndex(closestIndex);
      }

      if (isProgrammaticScroll.current) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        if (!containerRef.current || !isExpanded) return;
        const item = itemRefs.current[closestIndex];
        if (item) {
          const targetScrollTop = item.offsetTop - 84;
          const currentScrollTop = containerRef.current.scrollTop;
          if (Math.abs(currentScrollTop - targetScrollTop) > 1) {
            scrollToIndex(closestIndex);
          }
        }
      }, 100);
    });
  };

  const handleUserInteraction = () => {
    isProgrammaticScroll.current = false;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    if (scrollAnimationRef.current) {
      scrollAnimationRef.current.stop();
      scrollAnimationRef.current = null;
    }
  };

  useEffect(() => {
    if (!isExpanded) {
      setActiveIndex(0);
      if (containerRef.current) {
        containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [isExpanded]);

  const mobileHeader = (
    <div className="flex items-center px-4 pt-4 pb-4 relative bg-white sm:hidden">
      <button
        onClick={onClose}
        className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 hover:bg-gray-50 z-10"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 10L2 10M2 10L7.71429 15M2 10L7.71429 5"
            stroke="#3C3C43"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <h3 className="absolute inset-x-0 text-center text-base font-semibold text-[#3C3C43]">
        Hậu cung
      </h3>
    </div>
  );

  if (loading) {
    return (
      <PanelRoot>
        {mobileHeader}
        <StateBubble>
          <div className="flex-1 flex items-center justify-center h-[50px]">
            <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        </StateBubble>
      </PanelRoot>
    );
  }

  if (error) {
    return (
      <PanelRoot>
        {mobileHeader}
        <StateBubble>
          <span className="text-sm text-red-500 text-center">{error}</span>
        </StateBubble>
      </PanelRoot>
    );
  }

  if (notifications.length === 0) {
    return (
      <PanelRoot>
        {mobileHeader}
        <StateBubble>
          <p className="text-[16px] leading-[24px] text-[#111827] font[400] mb-[8px]">
            Hi bạn, mình đang túc trực 24/7 ở đây.
          </p>
          <p className="text-[16px] leading-[24px] text-[#111827] font[400] mb-[8px]">
            Dù là dòng tiền cá mập, nội bộ gom xả, hay các rủi ro bất ngờ, mình
            đều sẽ <b> quét dữ liệu</b> và <b>cảnh báo ngay</b> để bạn đi trước
            đám đông và bảo vệ tài khoản an toàn.
          </p>
          <p className="text-[16px] leading-[24px] text-[#111827] font[400] mb-[8px]">
            Bạn cứ yên tâm làm việc nhé, có "biến" mình báo ngay!
          </p>
        </StateBubble>
      </PanelRoot>
    );
  }

  const firstNotification = notifications[0];
  const firstIsRead = firstNotification.readStatus;
  const firstShowTail = !isExpanded || activeIndex === 0;

  return (
    <PanelRoot isShowBackground={isExpanded}>
      {mobileHeader}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        onWheel={handleUserInteraction}
        onTouchMove={handleUserInteraction}
        className={`flex-1 pt-[8px] sm:pt-[84px] pb-[calc(100vh-132px)] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] w-full ${isExpanded ? "overflow-y-auto" : "overflow-y-auto sm:overflow-y-hidden"}`}
      >
        {/* First Item (Always Visible) */}
        <div
          ref={(el) => (itemRefs.current[0] = el)}
          className="relative flex flex-col gap-1.5 w-full pr-4 pl-4 mb-[12px]"
        >
          <span
            className={`${getTimeAgoColor(firstIsRead)} text-[12px] text-zinc-500 font-medium px-[16px]`}
          >
            {timeAgo(firstNotification.createdDate)}
          </span>
          <div className="relative group">
            {/* Stack Layers (Visible only when collapsed) */}
            <AnimatePresence>
              {!isExpanded && notifications.length > 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    className={`absolute left-0 right-0 -bottom-[10px] h-full ${getCardBg(firstIsRead)} border border-[#ECECED] rounded-[16px] scale-x-[0.90] z-0`}
                  ></div>
                  <div
                    className={`absolute left-0 right-0 -bottom-[5px] h-full ${getCardBg(firstIsRead)} border border-[#ECECED] rounded-[16px] scale-x-[0.95] z-0`}
                  ></div>
                </motion.div>
              )}
            </AnimatePresence>

            <NotificationCardContent
              notification={firstNotification}
              showTail={firstShowTail}
              isRead={firstIsRead}
              onClick={() => handleClick(firstNotification)}
            />

            {/* Expand Button (Visible only when collapsed) */}
            <AnimatePresence>
              {!isExpanded && notifications.length > 1 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={() => setIsExpanded(true)}
                  className="hidden sm:flex absolute -bottom-4 left-4 w-8 h-8 bg-white border border-zinc-200 rounded-full items-center justify-center text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 shadow-sm z-20 transition-transform hover:scale-105"
                >
                  <ArrowDown size={16} strokeWidth={2.5} />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Rest of the Items */}
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                hidden: {
                  opacity: 0,
                  height: 0,
                  transition: { duration: 0.3, ease: "easeInOut" },
                },
                visible: {
                  opacity: 1,
                  height: "auto",
                  transition: {
                    duration: 0.4,
                    ease: [0.16, 1, 0.3, 1],
                    staggerChildren: 0.06,
                  },
                },
              }}
              className="flex flex-col gap-[12px] overflow-hidden"
            >
              {notifications.slice(1).map((notification, index) => {
                const actualIndex = index + 1;
                const isRead = notification.readStatus;
                return (
                  <motion.div
                    key={notification.id}
                    variants={itemVariants}
                    ref={(el) => (itemRefs.current[actualIndex] = el)}
                    className="relative flex flex-col gap-[4px] w-full pr-4 pl-4"
                  >
                    <span
                      className={`${getTimeAgoColor(isRead)} text-[12px] text-zinc-500 font-medium px-[16px]`}
                    >
                      {timeAgo(notification.createdDate)}
                    </span>
                    <div className="relative group">
                      <NotificationCardContent
                        notification={notification}
                        showTail={activeIndex === actualIndex}
                        isRead={isRead}
                        onClick={() => handleClick(notification)}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating Collapse Button (Bottom Left) */}
      <AnimatePresence>
        {isExpanded && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={() => setIsExpanded(false)}
            className="hidden sm:flex absolute bottom-6 left-4 w-10 h-10 bg-white/90 backdrop-blur-md border border-zinc-200 rounded-full items-center justify-center text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 shadow-md z-30 transition-transform hover:scale-105"
            title="Collapse"
          >
            <ArrowUp size={20} strokeWidth={2} />
          </motion.button>
        )}
      </AnimatePresence>
    </PanelRoot>
  );
}

export default NotificationPanel;
